import React, { useState, useEffect, useContext } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFolderOpen,
  faPlus,
  faEdit,
  faTrash,
  faChevronDown,
  faChevronRight,
  faPalette,
  faTag,
  faFilter,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { SettingsContext } from '../contexts/SettingsContext';
import {
  createFolder,
  getFolders,
  updateFolder,
  deleteFolder,
  addConversationToFolder,
  removeConversationFromFolder
} from '../utils/firestore';
import Toast from './Toast';

const ManagerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(249, 252, 255, 0.05) 100%);
  border-radius: var(--radius-xl);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const ManagerHeader = styled.div`
  padding: var(--space-4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  font-family: var(--font-heading-bold);
  background: linear-gradient(135deg, var(--text-color), var(--primary-600));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;

  &:hover {
    background: linear-gradient(135deg, var(--primary-50), var(--primary-100));
    color: var(--primary-600);
    transform: scale(1.05);
    box-shadow: var(--shadow-sm);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const SearchContainer = styled.div`
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-color-light);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  background: var(--background-color);
  color: var(--text-color);
  font-size: var(--text-sm);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const FolderList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-2);
`;

const FolderItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  ${props => props.isActive && css`
    background: var(--primary-50);
    border: 1px solid var(--primary-200);
  `}

  &:hover {
    background: var(--neutral-50);
  }

  ${props => props.isEditing && css`
    background: var(--primary-50);
    border: 1px solid var(--primary-300);
  `}
`;

const FolderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-lg);
  background: ${props => props.color || 'var(--primary-100)'};
  color: ${props => props.color ? 'white' : 'var(--primary-600)'};
  font-size: var(--text-sm);
  font-weight: 600;
  flex-shrink: 0;
`;

const FolderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FolderName = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: var(--space-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FolderMeta = styled.div`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const FolderActions = styled.div`
  display: flex;
  gap: var(--space-1);
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${FolderItem}:hover & {
    opacity: 1;
  }
`;

const FolderActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--text-xs);

  &:hover {
    background: var(--neutral-100);
    color: var(--text-color);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--text-xs);

  &:hover {
    background: var(--neutral-50);
    color: var(--text-color);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-8);
  color: var(--text-secondary);
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: var(--text-4xl);
  margin-bottom: var(--space-4);
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: var(--text-sm);
  margin-bottom: var(--space-4);
`;

const CreateButton = styled.button`
  padding: var(--space-3) var(--space-4);
  background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(135deg, var(--primary-700), var(--primary-800));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const FolderForm = styled.div`
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-color-light);
  background: var(--primary-50);
`;

const FormInput = styled.input`
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-md);
  background: white;
  color: var(--text-color);
  font-size: var(--text-sm);
  margin-bottom: var(--space-3);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 2px var(--primary-100);
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  flex-wrap: wrap;
`;

const ColorOption = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? 'var(--primary-600)' : 'transparent'};
  background: ${props => props.color};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: scale(1.1);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
`;

const FormButton = styled.button`
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${props => props.primary && css`
    background: var(--primary-600);
    color: white;
    border: 1px solid var(--primary-600);

    &:hover {
      background: var(--primary-700);
    }
  `}

  ${props => props.secondary && css`
    background: white;
    color: var(--text-color);
    border: 1px solid var(--border-color-light);

    &:hover {
      background: var(--neutral-50);
    }
  `}

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const FOLDER_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
];

function FolderManager({ userId, onFolderSelect, selectedFolderId = null }) {
  const [folders, setFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadFolders = async () => {
    try {
      setLoading(true);
      const folderList = await getFolders(userId);
      setFolders(folderList);
    } catch (error) {
      console.error('Error loading folders:', error);
      showToast('Failed to load folders', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadFolders();
    }
  }, [userId]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showToast('Please enter a folder name', 'error');
      return;
    }

    try {
      const folderData = {
        name: newFolderName.trim(),
        color: newFolderColor,
        description: '',
        parentFolderId: null
      };

      const folderId = await createFolder(userId, folderData);
      showToast('Folder created successfully', 'success');

      setNewFolderName('');
      setNewFolderColor(FOLDER_COLORS[0]);
      setIsCreating(false);
      await loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      showToast('Failed to create folder', 'error');
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !newFolderName.trim()) {
      showToast('Please enter a folder name', 'error');
      return;
    }

    try {
      const folderData = {
        name: newFolderName.trim(),
        color: newFolderColor
      };

      await updateFolder(userId, editingFolder.id, folderData);
      showToast('Folder updated successfully', 'success');

      setEditingFolder(null);
      setNewFolderName('');
      setNewFolderColor(FOLDER_COLORS[0]);
      await loadFolders();
    } catch (error) {
      console.error('Error updating folder:', error);
      showToast('Failed to update folder', 'error');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Are you sure you want to delete this folder? All conversations will be moved to the root.')) {
      return;
    }

    try {
      await deleteFolder(userId, folderId);
      showToast('Folder deleted successfully', 'success');
      await loadFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToast('Failed to delete folder', 'error');
    }
  };

  const startEdit = (folder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setNewFolderColor(folder.color || FOLDER_COLORS[0]);
  };

  const cancelEdit = () => {
    setEditingFolder(null);
    setNewFolderName('');
    setNewFolderColor(FOLDER_COLORS[0]);
  };

  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <ManagerContainer>
        <ManagerHeader>
          <HeaderTitle>Folders</HeaderTitle>
        </ManagerHeader>
        <EmptyState>
          <EmptyIcon>
            <FontAwesomeIcon icon={faFolder} />
          </EmptyIcon>
          <EmptyText>Loading folders...</EmptyText>
        </EmptyState>
      </ManagerContainer>
    );
  }

  return (
    <>
      <ManagerContainer>
        <ManagerHeader>
          <HeaderTitle>Folders</HeaderTitle>
          <HeaderActions>
            <ActionButton onClick={() => setIsCreating(true)} title="Create Folder">
              <FontAwesomeIcon icon={faPlus} />
            </ActionButton>
          </HeaderActions>
        </ManagerHeader>

        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search folders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        {(isCreating || editingFolder) && (
          <FolderForm>
            <FormInput
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  editingFolder ? handleUpdateFolder() : handleCreateFolder();
                }
              }}
            />

            <ColorPicker>
              {FOLDER_COLORS.map(color => (
                <ColorOption
                  key={color}
                  color={color}
                  selected={newFolderColor === color}
                  onClick={() => setNewFolderColor(color)}
                />
              ))}
            </ColorPicker>

            <FormActions>
              <FormButton secondary onClick={editingFolder ? cancelEdit : () => setIsCreating(false)}>
                Cancel
              </FormButton>
              <FormButton primary onClick={editingFolder ? handleUpdateFolder : handleCreateFolder}>
                {editingFolder ? 'Update' : 'Create'}
              </FormButton>
            </FormActions>
          </FolderForm>
        )}

        <FolderList>
          {filteredFolders.length === 0 && !isCreating ? (
            <EmptyState>
              <EmptyIcon>
                <FontAwesomeIcon icon={faFolder} />
              </EmptyIcon>
              <EmptyText>
                {searchTerm ? 'No folders found' : 'No folders yet'}
              </EmptyText>
              {!searchTerm && (
                <CreateButton onClick={() => setIsCreating(true)}>
                  Create Your First Folder
                </CreateButton>
              )}
            </EmptyState>
          ) : (
            filteredFolders.map(folder => (
              <FolderItem
                key={folder.id}
                isActive={selectedFolderId === folder.id}
                isEditing={editingFolder?.id === folder.id}
                onClick={() => onFolderSelect && onFolderSelect(folder)}
              >
                <FolderIcon color={folder.color}>
                  {folder.name.charAt(0).toUpperCase()}
                </FolderIcon>

                <FolderContent>
                  <FolderName>{folder.name}</FolderName>
                  <FolderMeta>
                    <span>{folder.conversationIds?.length || 0} conversations</span>
                  </FolderMeta>
                </FolderContent>

                <FolderActions>
                  <FolderActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(folder);
                    }}
                    title="Edit folder"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </FolderActionButton>
                  <FolderActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder.id);
                    }}
                    title="Delete folder"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </FolderActionButton>
                </FolderActions>
              </FolderItem>
            ))
          )}
        </FolderList>
      </ManagerContainer>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default FolderManager;
